import { useEffect, useState } from 'react'
import { supabase } from '../../lib/supabase'
import DashboardLayout from '../../components/dashboard/DashboardLayout'
import PageHeader from '../../components/dashboard/PageHeader'
import Card from '../../components/dashboard/Card'
import LoadingSpinner from '../../components/LoadingSpinner'
import { CaretRight, CaretDown, Users, Plus, PencilSimple, Trash, MagnifyingGlass, Camera, Eye, UsersFour, UsersThree } from '@phosphor-icons/react'

export default function HierarchyPage() {
  const [teams, setTeams] = useState([])
  const [loading, setLoading] = useState(true)
  const [orgAdmin, setOrgAdmin] = useState(null)
  const [orgName, setOrgName] = useState('')
  const [orgLogo, setOrgLogo] = useState(null)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [selectedItem, setSelectedItem] = useState(null)
  const [formData, setFormData] = useState({ name: '', parent_id: '', depth: 1, has_scan_permission: false })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  
  // Member drawer state
  const [showMemberDrawer, setShowMemberDrawer] = useState(false)
  const [selectedSubgroup, setSelectedSubgroup] = useState(null)
  
  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [itemToDelete, setItemToDelete] = useState(null)
  const [moveTargetId, setMoveTargetId] = useState('')
  const [availableTargets, setAvailableTargets] = useState([])
  const [deleting, setDeleting] = useState(false)
  
  // Collapsed state for each level
  const [collapsedTeams, setCollapsedTeams] = useState({})
  const [collapsedGroups, setCollapsedGroups] = useState({})
  const [collapsedSubgroups, setCollapsedSubgroups] = useState({})

  useEffect(() => {
    loadAll()
  }, [])

  const loadAll = async () => {
    setLoading(true)
    try {
      const { data: { user } } = await supabase.auth.getUser()
      const { data: userData } = await supabase
        .from('users')
        .select('org_id')
        .eq('id', user.id)
        .single()

      // Load organization info
      const { data: orgData } = await supabase
        .from('organizations')
        .select('name, logo_url')
        .eq('id', userData.org_id)
        .single()
      
      setOrgName(orgData?.name || 'Organization')
      setOrgLogo(orgData?.logo_url || null)

      // Load org admin
      const { data: adminData } = await supabase
        .from('users')
        .select('id, name, email, profile_photo_url, avatar_url')
        .eq('org_id', userData.org_id)
        .eq('role', 'org_admin')
        .maybeSingle()
      
      setOrgAdmin(adminData)

      // Load all hierarchy with tree structure
      const teamsData = await loadHierarchyTree(userData.org_id)
      setTeams(teamsData)
      
      // Initialize all items as collapsed
      const teamsCollapsed = {}
      const groupsCollapsed = {}
      const subgroupsCollapsed = {}
      
      teamsData.forEach(team => {
        teamsCollapsed[team.id] = true
        team.groups?.forEach(group => {
          groupsCollapsed[group.id] = true
          group.subgroups?.forEach(subgroup => {
            subgroupsCollapsed[subgroup.id] = true
          })
        })
      })
      
      setCollapsedTeams(teamsCollapsed)
      setCollapsedGroups(groupsCollapsed)
      setCollapsedSubgroups(subgroupsCollapsed)
    } catch (error) {
      console.error('Load hierarchy error:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadHierarchyTree = async (orgId) => {
    // Load all teams
    const { data: teamsData } = await supabase
      .from('groups')
      .select('*')
      .eq('org_id', orgId)
      .eq('depth', 1)
      .order('name')

    // For each team, load its groups, and for each group load its subgroups
    const teamsWithHierarchy = await Promise.all(
      (teamsData || []).map(async (team) => {
        // Get team leader
        const { data: teamLeader } = await supabase
          .from('group_members')
          .select('*, users!inner(id, name, email, profile_photo_url, avatar_url, leadership_level)')
          .eq('group_id', team.id)
          .eq('users.leadership_level', 'team')
          .limit(1)
          .maybeSingle()

        // Count ALL members directly assigned to this team
        const { data: teamMembers } = await supabase
          .from('group_members')
          .select('user_id')
          .eq('group_id', team.id)
        const teamMemberCount = teamMembers?.length || 0

        // Load groups under this team
        const { data: groupsData } = await supabase
          .from('groups')
          .select('*')
          .eq('parent_id', team.id)
          .eq('depth', 2)
          .order('name')

        // For each group, load subgroups
        const groupsWithSubgroups = await Promise.all(
          (groupsData || []).map(async (group) => {
            // Get group leader
            const { data: groupLeader } = await supabase
              .from('group_members')
              .select('*, users!inner(id, name, email, profile_photo_url, avatar_url, leadership_level)')
              .eq('group_id', group.id)
              .eq('users.leadership_level', 'group')
              .limit(1)
              .maybeSingle()

            // Count ALL members directly assigned to this group
            const { data: groupMembers } = await supabase
              .from('group_members')
              .select('user_id')
              .eq('group_id', group.id)
            const groupMemberCount = groupMembers?.length || 0

            // Load subgroups under this group
            const { data: subgroupsData } = await supabase
              .from('groups')
              .select('*')
              .eq('parent_id', group.id)
              .eq('depth', 3)
              .order('name')

            // For each subgroup, get leader and member count
            const subgroupsWithDetails = await Promise.all(
              (subgroupsData || []).map(async (subgroup) => {
                // Get subgroup leader
                const { data: subgroupLeader } = await supabase
                  .from('group_members')
                  .select('*, users!inner(id, name, email, profile_photo_url, avatar_url, leadership_level)')
                  .eq('group_id', subgroup.id)
                  .eq('users.leadership_level', 'subgroup')
                  .limit(1)
                  .maybeSingle()

                // Get all members (excluding leader)
                const { data: members } = await supabase
                  .from('group_members')
                  .select('*, users!inner(id, name, email, profile_photo_url, avatar_url, leadership_level)')
                  .eq('group_id', subgroup.id)
                  .neq('users.leadership_level', 'subgroup')

                // memberCount includes leader + members
                const totalMembers = (members?.length || 0) + (subgroupLeader ? 1 : 0)

                return {
                  ...subgroup,
                  leader: subgroupLeader?.users || null,
                  members: members?.map(m => m.users) || [],
                  memberCount: totalMembers
                }
              })
            )

            return {
              ...group,
              leader: groupLeader?.users || null,
              memberCount: groupMemberCount,
              subgroups: subgroupsWithDetails
            }
          })
        )

        return {
          ...team,
          leader: teamLeader?.users || null,
          memberCount: teamMemberCount,
          groups: groupsWithSubgroups
        }
      })
    )

    return teamsWithHierarchy
  }

  const toggleTeam = (teamId) => {
    setCollapsedTeams(prev => {
      const isCurrentlyClosed = prev[teamId] === true || prev[teamId] === undefined
      
      if (isCurrentlyClosed) {
        // If currently closed, close all others and open this one
        const newState = {}
        teams.forEach(team => {
          newState[team.id] = true // Close all
        })
        newState[teamId] = false // Open clicked one
        return newState
      } else {
        // If currently open, close it
        return { ...prev, [teamId]: true }
      }
    })
  }

  const toggleGroup = (groupId) => {
    setCollapsedGroups(prev => {
      const isCurrentlyClosed = prev[groupId] === true || prev[groupId] === undefined
      
      if (isCurrentlyClosed) {
        // If currently closed, close all others and open this one
        const newState = {}
        teams.forEach(team => {
          team.groups?.forEach(group => {
            newState[group.id] = true // Close all
          })
        })
        newState[groupId] = false // Open clicked one
        return newState
      } else {
        // If currently open, close it
        return { ...prev, [groupId]: true }
      }
    })
  }

  const toggleSubgroup = (subgroupId) => {
    setCollapsedSubgroups(prev => {
      const isCurrentlyClosed = prev[subgroupId] === true || prev[subgroupId] === undefined
      
      if (isCurrentlyClosed) {
        // If currently closed, close all others and open this one
        const newState = {}
        teams.forEach(team => {
          team.groups?.forEach(group => {
            group.subgroups?.forEach(subgroup => {
              newState[subgroup.id] = true // Close all
            })
          })
        })
        newState[subgroupId] = false // Open clicked one
        return newState
      } else {
        // If currently open, close it
        return { ...prev, [subgroupId]: true }
      }
    })
  }

  const handleCreate = async (e) => {
    e.preventDefault()
    setError('')
    setSaving(true)

    try {
      const { data: { user } } = await supabase.auth.getUser()
      const { data: userData } = await supabase
        .from('users')
        .select('org_id')
        .eq('id', user.id)
        .single()

      await supabase
        .from('groups')
        .insert({
          org_id: userData.org_id,
          name: formData.name,
          depth: formData.depth,
          parent_id: formData.parent_id || null,
          has_scan_permission: formData.depth === 1 ? formData.has_scan_permission : false
        })

      setShowCreateModal(false)
      setFormData({ name: '', parent_id: '', depth: 1, has_scan_permission: false })
      await loadAll()
    } catch (error) {
      console.error('Create error:', error)
      setError(error.message)
    } finally {
      setSaving(false)
    }
  }

  const handleEdit = async (e) => {
    e.preventDefault()
    setError('')
    setSaving(true)

    try {
      const updateData = { name: formData.name }
      
      // Only update has_scan_permission for teams (depth 1)
      if (selectedItem.depth === 1) {
        updateData.has_scan_permission = formData.has_scan_permission
      }

      await supabase
        .from('groups')
        .update(updateData)
        .eq('id', selectedItem.id)

      setShowEditModal(false)
      setSelectedItem(null)
      setFormData({ name: '', parent_id: '', depth: 1, has_scan_permission: false })
      await loadAll()
    } catch (error) {
      console.error('Edit error:', error)
      setError(error.message)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (item) => {
    const itemType = item.depth === 1 ? 'team' : item.depth === 2 ? 'group' : 'subgroup'
    
    try {
      let targets = []
      
      if (itemType === 'subgroup') {
        // Get other subgroups in the same group
        const { data } = await supabase
          .from('groups')
          .select('id, name')
          .eq('parent_id', item.parent_id)
          .eq('depth', 3)
          .neq('id', item.id)
        targets = data || []
      } else if (itemType === 'group') {
        // Get other groups in the same team
        const { data } = await supabase
          .from('groups')
          .select('id, name')
          .eq('parent_id', item.parent_id)
          .eq('depth', 2)
          .neq('id', item.id)
        targets = data || []
      } else if (itemType === 'team') {
        // Get other teams in the same org
        const { data: { user } } = await supabase.auth.getUser()
        const { data: userData } = await supabase
          .from('users')
          .select('org_id')
          .eq('id', user.id)
          .single()
        
        const { data } = await supabase
          .from('groups')
          .select('id, name')
          .eq('org_id', userData.org_id)
          .eq('depth', 1)
          .neq('id', item.id)
        targets = data || []
      }
      
      if (targets.length === 0) {
        alert(`Cannot delete this ${itemType}. You need at least one other ${itemType} to move the contents to.`)
        return
      }
      
      setItemToDelete(item)
      setAvailableTargets(targets)
      setMoveTargetId(targets[0]?.id || '')
      setShowDeleteModal(true)
      
    } catch (error) {
      console.error('Error fetching targets:', error)
      alert('Failed to prepare deletion: ' + error.message)
    }
  }

  const confirmDelete = async () => {
    if (!itemToDelete || !moveTargetId) return
    
    const itemType = itemToDelete.depth === 1 ? 'team' : itemToDelete.depth === 2 ? 'group' : 'subgroup'
    setDeleting(true)
    setError('')
    
    try {
      if (itemType === 'subgroup') {
        // SUBGROUP: Move members to target subgroup
        // Get all members including leader
        const { data: members } = await supabase
          .from('group_members')
          .select('user_id')
          .eq('group_id', itemToDelete.id)
        
        if (members && members.length > 0) {
          // Move members to target subgroup
          for (const member of members) {
            // Check if member already exists in target
            const { data: existing } = await supabase
              .from('group_members')
              .select('id')
              .eq('user_id', member.user_id)
              .eq('group_id', moveTargetId)
              .single()
            
            if (!existing) {
              await supabase
                .from('group_members')
                .insert({
                  user_id: member.user_id,
                  group_id: moveTargetId
                })
            }
          }
          
          // Remove leader role from the subgroup leader (becomes regular member)
          if (itemToDelete.leader_id) {
            await supabase
              .from('users')
              .update({ leadership_level: null })
              .eq('id', itemToDelete.leader_id)
          }
        }
        
        // Delete old group_members entries
        await supabase
          .from('group_members')
          .delete()
          .eq('group_id', itemToDelete.id)
        
      } else if (itemType === 'group') {
        // GROUP: Move subgroups to target group (preserve structure)
        const { data: subgroups } = await supabase
          .from('groups')
          .select('id')
          .eq('parent_id', itemToDelete.id)
          .eq('depth', 3)
        
        if (subgroups && subgroups.length > 0) {
          // Move all subgroups to new parent
          await supabase
            .from('groups')
            .update({ parent_id: moveTargetId })
            .eq('parent_id', itemToDelete.id)
        }
        
      } else if (itemType === 'team') {
        // TEAM: Move groups to target team (preserve structure)
        const { data: groups } = await supabase
          .from('groups')
          .select('id')
          .eq('parent_id', itemToDelete.id)
          .eq('depth', 2)
        
        if (groups && groups.length > 0) {
          // Move all groups to new parent
          await supabase
            .from('groups')
            .update({ parent_id: moveTargetId })
            .eq('parent_id', itemToDelete.id)
        }
      }
      
      // Delete the item itself
      await supabase
        .from('groups')
        .delete()
        .eq('id', itemToDelete.id)
      
      setShowDeleteModal(false)
      setItemToDelete(null)
      setMoveTargetId('')
      setAvailableTargets([])
      await loadAll()
      
    } catch (error) {
      console.error('Delete error:', error)
      setError('Failed to delete: ' + error.message)
    } finally {
      setDeleting(false)
    }
  }

  const openCreateModal = (depth, parentId = '') => {
    setFormData({ name: '', parent_id: parentId, depth })
    setError('')
    setShowCreateModal(true)
  }

  const getParentOptions = () => {
    if (formData.depth === 2) {
      // Creating a group - show teams
      return teams
    } else if (formData.depth === 3) {
      // Creating a subgroup - show groups
      const allGroups = []
      teams.forEach(team => {
        if (team.groups) {
          allGroups.push(...team.groups)
        }
      })
      return allGroups
    }
    return []
  }

  const openEditModal = (item) => {
    setSelectedItem(item)
    setFormData({ 
      name: item.name, 
      parent_id: item.parent_id || '', 
      depth: item.depth,
      has_scan_permission: item.has_scan_permission || false
    })
    setError('')
    setShowEditModal(true)
  }

  if (loading) {
    return <LoadingSpinner message="Loading hierarchy..." />
  }

  const itemTypeLabel = formData.depth === 1 ? 'Team' : formData.depth === 2 ? 'Group' : 'Subgroup'

  // Filter teams based on search term
  const filteredTeams = teams.filter(team => {
    const searchLower = searchTerm.toLowerCase()
    
    // Search in team name
    if (team.name.toLowerCase().includes(searchLower)) return true
    
    // Search in team leader name
    if (team.leader?.name.toLowerCase().includes(searchLower)) return true
    
    // Search in groups
    if (team.groups?.some(group => {
      // Search in group name
      if (group.name.toLowerCase().includes(searchLower)) return true
      
      // Search in group leader name
      if (group.leader?.name.toLowerCase().includes(searchLower)) return true
      
      // Search in subgroups
      if (group.subgroups?.some(subgroup => {
        // Search in subgroup name
        if (subgroup.name.toLowerCase().includes(searchLower)) return true
        
        // Search in subgroup leader name
        if (subgroup.leader?.name.toLowerCase().includes(searchLower)) return true
        
        // Search in member names
        if (subgroup.members?.some(member => 
          member.name.toLowerCase().includes(searchLower) || 
          member.email.toLowerCase().includes(searchLower)
        )) return true
        
        return false
      })) return true
      
      return false
    })) return true
    
    return false
  })

  return (
    <DashboardLayout>
      <PageHeader
        title="Organization structure"
        description="Manage your organization structure"
      >
        <div className="flex items-center gap-2">
          <button
            onClick={() => openCreateModal(1)}
            className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
          >
            <Users size={20} weight="light" />
            Add Team
          </button>
          <button
            onClick={() => openCreateModal(2)}
            className="flex items-center gap-2 px-6 py-3 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <UsersThree size={20} weight="light" />
            Add Sub Team
          </button>
          <button
            onClick={() => openCreateModal(3)}
            className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-6 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <UsersFour size={20} weight="light" />
            Add Department
          </button>
        </div>
      </PageHeader>

      <div className="p-6 space-y-6">
        {/* Single Container: Organization Info + Kanban Grid */}
        <Card>
          {/* Organization Info Section */}
          <div className="p-6">
            <div className="flex items-center justify-between">
              {/* Left: Logo + Org Name + Admin Name */}
              <div className="flex items-center gap-4">
                {/* Logo */}
                {orgLogo ? (
                  <img 
                    src={orgLogo} 
                    alt={orgName}
                    className="flex-shrink-0 w-24 h-24 rounded-full object-cover"
                  />
                ) : (
                  <div className="flex-shrink-0 w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center">
                    <span className="text-gray-600 font-bold text-2xl">
                      {orgName?.charAt(0) || 'O'}
                    </span>
                  </div>
                )}

                {/* Org Info */}
                <div>
                  <h3 className="text-2xl font-medium text-[#1e293b]">{orgName}</h3>
                  <p className="text-base text-[#475569]">
                    Admin: <span className="text-[#1e293b] font-medium">{orgAdmin?.name || 'Not assigned'}</span>
                  </p>
                  {/* Team Count Pill */}
                  <div className="mt-2">
                    <div className="inline-flex px-3 py-1 border b-[#e2e8f0] bg-[#f8fafc] text-[#64748b] rounded-full text-sm font-regular">
                      Number of teams: <span className="font-semibold ml-1 text-[#1e293b]">{teams.length}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right: Search Only */}
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search for a team"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-64 pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <MagnifyingGlass
                  size={20}
                  weight="light"
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                />
              </div>
            </div>
          </div>

          {/* Separator Line */}
          <div className="border-b border-[#e2e8f0]"></div>

          {/* Kanban Grid Section */}
          <div className="p-6">
            {filteredTeams.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="mx-auto mb-3 text-gray-300">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                </svg>
                <p className="font-medium">{searchTerm ? 'No matches found' : 'No teams yet'}</p>
                <p className="text-sm mt-1">{searchTerm ? 'Try a different search term' : 'Click "Add Team" to get started'}</p>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-6">
                {filteredTeams.map((team, teamIndex) => {
      // Color rotation for team cards
      const teamColors = ['bg-blue-50', 'bg-green-50', 'bg-pink-50']
      const teamColor = teamColors[teamIndex % 3]

                  return (
                    <div key={team.id} className={`${teamColor} rounded-lg shadow flex flex-col h-[500px]`}>
                      {/* Team Header - Fixed */}
                      <div className="p-5 pb-0 flex-shrink-0">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <h3 className="text-xl font-semibold text-[#1E293B]">{team.name}</h3>
                            {/* Camera icon for teams with scanning ability */}
                            {team.has_scan_permission && (
                              <div className="relative group">
                                <Camera size={20} weight="fill" className="text-[#1e293b]" />
                                {/* Tooltip */}
                                <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-3 py-1.5 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                                  Members in this team can scan QR codes for attendance marking
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => openEditModal(team)}
                              className="p-1.5 text-[#000000] hover:bg-white/50 rounded transition-colors"
                              title="Edit Team"
                            >
                              <PencilSimple size={20} weight="light" />
                            </button>
                            <button
                              onClick={() => handleDelete(team)}
                              className="p-1.5 text-[#64748b] hover:bg-white/50 rounded transition-colors"
                              title="Delete Team"
                            >
                              <Trash size={20} weight="light" />
                            </button>
                          </div>
                        </div>

                        <div className="space-y-2 text-sm">
                          <div className="flex items-center gap-2">
                            <span className="text-[#475569]">Head:</span>
                            {/* No avatar - just name */}
                            {team.leader ? (
                              <span className="font-regular text-[#1e293b]">{team.leader.name}</span>
                            ) : (
                              <span className="text-gray-400 italic">Not assigned</span>
                            )}
                          </div>
                          {/* Total Members as Pill */}
                          <div>
                            <div className="inline-flex items-center gap-1.5 px-3 border b-[#e2e8f0] py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">
                              <span>Total Members:</span>
                              <span className="font-bold">
                                {
                                  (team.memberCount || 0) + 
                                  (team.groups?.reduce((sum, g) => 
                                    sum + (g.memberCount || 0) + 
                                    (g.subgroups?.reduce((subSum, sg) => subSum + (sg.memberCount || 0), 0) || 0)
                                  , 0) || 0)
                                }
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

          {/* Groups - Scrollable */}
          <div className="flex-1 overflow-y-auto p-5 pt-3">
            <div className="space-y-3">
            {team.groups && team.groups.length > 0 ? (
              team.groups.map((group) => (
                <div key={group.id} className="bg-white rounded-lg p-4 shadow-sm">
                  {/* Group Header */}
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 bg-[#d6e1ff] text-[#2248b2] rounded-full text-xs font-regular">
                        Sub team
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => openEditModal(group)}
                        className="p-1 text-[#1e293b] hover:bg-gray-100 rounded transition-colors"
                        title="Edit Group"
                      >
                        <PencilSimple size={16} weight="light" />
                      </button>
                      <button
                        onClick={() => handleDelete(group)}
                        className="p-1 text-[#64748b] hover:bg-gray-100 rounded transition-colors"
                        title="Delete Group"
                      >
                        <Trash size={16} weight="light" />
                      </button>
                    </div>
                  </div>

                  <div className="space-y-1">
                    <h4 className="font-semibold text-base text-[#1e293b]">{group.name}</h4>
                    <div className="text-xs text-[#475569] space-y-0.5">
                      <div className="flex items-center gap-1.5">
                        <span>Lead:</span>
                        {/* No avatar - just name */}
                        {group.leader ? (
                          <span className="font-regular text-[#1e293b]">{group.leader.name}</span>
                        ) : (
                          <span className="text-gray-400 italic">Not assigned</span>
                        )}
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <span>Total members: </span>
                          <span className="font-semibold text-gray-900">
                            {
                              (group.memberCount || 0) + 
                              (group.subgroups?.reduce((sum, sg) => sum + (sg.memberCount || 0), 0) || 0)
                            }
                          </span>
                        </div>
                        {/* Chevron at bottom-right */}
                        <button
                          onClick={() => toggleGroup(group.id)}
                          className="flex-shrink-0 p-1 hover:bg-gray-100 rounded transition-colors"
                        >
                          {collapsedGroups[group.id] ? (
                            <CaretRight size={16} weight="bold" className="text-gray-600" />
                          ) : (
                            <CaretDown size={16} weight="bold" className="text-gray-600" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Subgroups */}
                  {!collapsedGroups[group.id] && (
                    <>
                      {group.subgroups && group.subgroups.length > 0 ? (
                        <div className="mt-3 space-y-2">
                          {group.subgroups.map((subgroup) => (
                        <div key={subgroup.id} className="bg-[#f1f5f9] rounded-lg p-3 border border-[#e2e8f0]">
                          {/* Header: Badge + Edit/Delete */}
                          <div className="flex items-center justify-between mb-1">
                            <span className="px-2 py-0.5 bg-[#bfe0c8] text-[#1e6931] rounded-full text-xs font-regular">
                              Department
                            </span>
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => openEditModal(subgroup)}
                                className="p-1 text-[#1e293b] hover:bg-gray-100 rounded transition-colors"
                                title="Edit Subgroup"
                              >
                                <PencilSimple size={14} weight="light" />
                              </button>
                              <button
                                onClick={() => handleDelete(subgroup)}
                                className="p-1 text-[#64748b] hover:bg-gray-100 rounded transition-colors"
                                title="Delete Subgroup"
                              >
                                <Trash size={14} weight="light" />
                              </button>
                            </div>
                          </div>

                          {/* Content */}
                          <div className="space-y-0.5">
                            <h5 className="font-medium text-[#1e293b] text-base">{subgroup.name}</h5>
                            <div className="text-xs text-[#475569]">
                              <div>
                                <span>H.O.D: </span>
                                {subgroup.leader ? (
                                  <span className="font-medium text-[#1e293b]">{subgroup.leader.name}</span>
                                ) : (
                                  <span className="text-gray-400 italic">Not assigned</span>
                                )}
                              </div>
                              
                              {/* Total Members + Eye Icon */}
                              <div className="flex items-center justify-between mt-1">
                                <div>
                                  <span>Total member(s): </span>
                                  <span className="font-semibold text-[#1e293b]">{subgroup.memberCount}</span>
                                </div>
                                <button
                                  onClick={() => {
                                    if (subgroup.memberCount > 0) {
                                      setSelectedSubgroup(subgroup)
                                      setShowMemberDrawer(true)
                                    }
                                  }}
                                  className={`p-1 rounded transition-colors ${
                                    subgroup.memberCount > 0 
                                      ? 'text-gray-600 hover:bg-gray-100 cursor-pointer' 
                                      : 'text-gray-300 cursor-not-allowed'
                                  }`}
                                  disabled={subgroup.memberCount === 0}
                                >
                                  <Eye size={16} weight="light" />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                      ) : (
                        <div className="mt-3 text-center py-8">
                          <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="mx-auto mb-3 text-gray-400">
                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                            <circle cx="9" cy="7" r="4"></circle>
                            <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                            <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                          </svg>
                          <p className="text-[#1e293b] font-medium text-sm">Add a department</p>
                          <p className="text-[#475569] text-xs">No department in this sub team</p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))
            ) : (
              <div className="text-center py-12">
                <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="mx-auto mb-4 text-gray-400">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                </svg>
                <p className="text-[#1e293b] font-medium text-base">Add a sub team</p>
                <p className="text-[#475569] text-sm ">No Sub team in this team.</p>
              </div>
            )}
            </div>
          </div>
        </div>
                  )
    })}
  </div>
)}
          </div>
        </Card>

        {/* Member Drawer */}
        {showMemberDrawer && selectedSubgroup && (
  <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-end">
    <div className="bg-white w-96 h-full shadow-xl overflow-y-auto">
      {/* Header */}
      <div className="sticky top-0 bg-white border-b border-gray-200 p-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-bold text-gray-900">Members</h3>
          <button
            onClick={() => setShowMemberDrawer(false)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
        <p className="text-sm text-gray-600">{selectedSubgroup.name}</p>
        <p className="text-xs text-gray-500 mt-1">{selectedSubgroup.memberCount} member(s)</p>
      </div>

      {/* Members List */}
      <div className="p-6 space-y-3">
        {selectedSubgroup.leader || (selectedSubgroup.members && selectedSubgroup.members.length > 0) ? (
          <>
            {/* Leader First */}
            {selectedSubgroup.leader && (
              <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                {selectedSubgroup.leader.profile_photo_url || selectedSubgroup.leader.avatar_url ? (
                  <img
                    src={selectedSubgroup.leader.profile_photo_url || selectedSubgroup.leader.avatar_url}
                    alt={selectedSubgroup.leader.name}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-blue-200 flex items-center justify-center">
                    <span className="text-blue-700 text-sm font-semibold">
                      {selectedSubgroup.leader.name?.charAt(0)}
                    </span>
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-gray-900 truncate">{selectedSubgroup.leader.name}</p>
                    <span className="px-2 py-0.5 bg-blue-600 text-white text-[10px] font-medium rounded-full">
                      Leader
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 truncate">{selectedSubgroup.leader.email}</p>
                </div>
              </div>
            )}
            
            {/* Regular Members */}
            {selectedSubgroup.members?.map((member) => (
              <div key={member.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                {member.profile_photo_url || member.avatar_url ? (
                  <img
                    src={member.profile_photo_url || member.avatar_url}
                    alt={member.name}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                    <span className="text-gray-600 text-sm font-semibold">
                      {member.name?.charAt(0)}
                    </span>
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">{member.name}</p>
                  <p className="text-sm text-gray-500 truncate">{member.email}</p>
                </div>
              </div>
            ))}
          </>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <p>No members in this subgroup</p>
          </div>
        )}
      </div>
    </div>
  </div>
)}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Create New {itemTypeLabel}</h2>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                {error}
              </div>
            )}

            <form onSubmit={handleCreate} className="space-y-4">
              {formData.depth > 1 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Parent {formData.depth === 2 ? 'Team' : 'Group'} *
                  </label>
                  <select
                    required
                    value={formData.parent_id}
                    onChange={(e) => setFormData({ ...formData, parent_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select {formData.depth === 2 ? 'team' : 'group'}...</option>
                    {getParentOptions().map(parent => (
                      <option key={parent.id} value={parent.id}>{parent.name}</option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {itemTypeLabel} Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={`e.g., ${formData.depth === 1 ? 'Programs' : formData.depth === 2 ? 'Creative' : 'Media'}`}
                />
              </div>

              {/* Scan Permission - Only for Teams */}
              {formData.depth === 1 && (
                <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <input
                    type="checkbox"
                    id="scan-permission"
                    checked={formData.has_scan_permission}
                    onChange={(e) => setFormData({ ...formData, has_scan_permission: e.target.checked })}
                    className="mt-0.5 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="scan-permission" className="text-sm text-gray-700">
                    <span className="font-medium">Enable QR scanning</span>
                    <p className="text-xs text-gray-600 mt-1">
                      Allow all members in this team (and its groups/subgroups) to scan QR codes for attendance
                    </p>
                  </label>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false)
                    setFormData({ name: '', parent_id: '', depth: 1, has_scan_permission: false })
                    setError('')
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {saving ? 'Creating...' : 'Create ' + itemTypeLabel}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Edit {itemTypeLabel}</h2>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                {error}
              </div>
            )}

            <form onSubmit={handleEdit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {itemTypeLabel} Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Scan Permission - Only for Teams */}
              {selectedItem?.depth === 1 && (
                <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <input
                    type="checkbox"
                    id="edit-scan-permission"
                    checked={formData.has_scan_permission}
                    onChange={(e) => setFormData({ ...formData, has_scan_permission: e.target.checked })}
                    className="mt-0.5 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="edit-scan-permission" className="text-sm text-gray-700">
                    <span className="font-medium">Enable QR scanning</span>
                    <p className="text-xs text-gray-600 mt-1">
                      Allow all members in this team (and its groups/subgroups) to scan QR codes for attendance
                    </p>
                  </label>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false)
                    setSelectedItem(null)
                    setFormData({ name: '', parent_id: '', depth: 1, has_scan_permission: false })
                    setError('')
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && itemToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-2">
              Delete {itemToDelete.depth === 1 ? 'Team' : itemToDelete.depth === 2 ? 'Group' : 'Subgroup'}
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              Deleting "<span className="font-semibold">{itemToDelete.name}</span>"
            </p>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                {error}
              </div>
            )}

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {itemToDelete.depth === 3 && 'Move members to which subgroup?'}
                {itemToDelete.depth === 2 && 'Move subgroups to which group?'}
                {itemToDelete.depth === 1 && 'Move groups to which team?'}
              </label>
              <select
                value={moveTargetId}
                onChange={(e) => setMoveTargetId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                {availableTargets.map(target => (
                  <option key={target.id} value={target.id}>
                    {target.name}
                  </option>
                ))}
              </select>
              <p className="mt-2 text-xs text-gray-500">
                {itemToDelete.depth === 3 && 'All members will be moved to the selected subgroup. The subgroup leader will become a regular member.'}
                {itemToDelete.depth === 2 && 'All subgroups and their members will be moved to the selected group. Structure will be preserved.'}
                {itemToDelete.depth === 1 && 'All groups, subgroups, and members will be moved to the selected team. Structure will be preserved.'}
              </p>
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowDeleteModal(false)
                  setItemToDelete(null)
                  setMoveTargetId('')
                  setAvailableTargets([])
                  setError('')
                }}
                disabled={deleting}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={deleting || !moveTargetId}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  )
}